/* eslint-disable no-template-curly-in-string */
import { Form, Input, Typography, Flex, notification } from "antd";
import { StyledButton } from "Components/Button/StyledButton";
import { TitleCardsList } from "Components/SelectableCards/SelectableCard";
import {
  AreaOfExpertiseOptions,
  GeoOptions,
  YearsOfExperienceOptions,
} from "./helpers";
import "./styles/profileForm.css";
import { useUserInfo } from "GlobalStates/User";
import { useMutation } from "@tanstack/react-query";
import { createProfile, generateSummary } from "./apis/createProfile";
import { ROUTES } from "Constants/AllRoutes";
import { useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";

const layout = {
  labelCol: { span: 24 },
  wrapperCol: { span: 24 },
};

const validateMessages = {
  required: "${label} is required!",
  types: {
    email: "${label} is not a valid email",
    number: "${label} is not a valid number",
  },
  number: { range: "${label} must be between ${min} and ${max}" },
};

export const ProfileForm = () => {
  const { user, setUser } = useUserInfo();
  const { Title } = Typography;
  const navigate = useNavigate();
  const [summary, setSummary] = useState();
  const [form] = Form.useForm();
  const areaOfExpertise = Form.useWatch(["user", "areaOfExpertise"], form);
  const geoOption = Form.useWatch(["user", "geoOptions"], form);
  const [api, contextHolder] = notification.useNotification();
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const { state } = useLocation();

  const createProfileMutation = useMutation({
    mutationFn: createProfile,
    onSuccess: () => {
      setUser((prev) => ({ ...prev, hasCompletedProfile: true }));
      navigate(ROUTES.MY_CALENDAR);
    },
  });

  const generateProfessionalSummary = useMutation({
    mutationFn: generateSummary,
    onSuccess: (data) => {
      setLoading(false);
      setSummary(data?.data?.summary);
      form.setFieldsValue({
        user: {
          ...user,
          professionalSummary: data?.data?.summary,
        },
      });
    },
    onError: () => {
      setLoading(false);
      api.open({
        message: "Error",
        description:
          "We have an error generating your summary. Please try again.",
      });
    },
  });

  const onFinish = ({ user }) => {
    if (user?.geoOptions === "Other") user.geoOptions = user.customGeo;
    if (user?.areaOfExpertise === "Other")
      user.areaOfExpertise = user.customExpertise;
    if (user?.cost === "Other") user.cost = user.customCost;
    if (user?.cost === "Free") user.cost = 0;

    createProfileMutation.mutate({ ...user, ...state });
  };

  return (
    <Flex
      vertical
      style={{ height: "100%" }}
      className="profile-form-container"
    >
      {contextHolder}

      {/* Header stays in flow so it cannot overlap */}
      <Flex align="center" style={{ padding: 16, flex: "0 0 auto" }}>
        <div className="navbar-logo-container mobile-menu-logo">
          <img
            src="https://stagingonegrad.blob.core.windows.net/fe-assets/logo.png"
            alt="logo"
            className="navbar-main-logo"
          />
        </div>
      </Flex>

      {/* Content takes remaining space and scrolls */}
      <Flex
        vertical
        align="center"
        style={{ flex: 1, overflow: "auto", padding: "0 16px 16px" }}
      >
        <Title level={2} style={{ letterSpacing: "1px" }}>
          Profile Form
        </Title>

        <Form
          initialValues={{ user: { name: user?.name } }}
          form={form}
          className="profile-form"
          layout="vertical"
          {...layout}
          name="nest-messages"
          onFinish={onFinish}
          validateMessages={validateMessages}
          style={{ width: "100%", maxWidth: 720 }}
        >
          <Form.Item name={["user", "name"]} label="Name">
            <Input value={user?.name} disabled />
          </Form.Item>

          <Form.Item
            name={["user", "profileURL"]}
            label="Linkedin Profile URL (optional for faster verification)"
            rules={[
              { required: false },
              { type: "url", message: "Please enter a valid URL" },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name={["user", "areaOfExpertise"]}
            label="Area of Expertise"
            rules={[{ required: true }]}
          >
            {/* @ts-ignore */}
            <TitleCardsList options={AreaOfExpertiseOptions} />
          </Form.Item>

          {areaOfExpertise === "Other" && (
            <Form.Item
              name={["user", "customExpertise"]}
              label="Please specify your area of expertise"
              rules={[{ required: true, message: "This field is required" }]}
            >
              <Input placeholder="Your area of expertise" />
            </Form.Item>
          )}

          <Form.Item
            name={["user", "geoOptions"]}
            label="Geo Options"
            rules={[{ required: true }]}
          >
            {/* @ts-ignore */}
            <TitleCardsList options={GeoOptions} />
          </Form.Item>

          {geoOption === "Other" && (
            <Form.Item
              name={["user", "customGeo"]}
              label="Please specify your geo option"
              rules={[{ required: true, message: "This field is required" }]}
            >
              <Input placeholder="Your geo option" />
            </Form.Item>
          )}

          <Form.Item
            name={["user", "yearsOfExperience"]}
            label="Years of Experience"
            rules={[{ required: true }]}
          >
            {/* @ts-ignore */}
            <TitleCardsList options={YearsOfExperienceOptions} />
          </Form.Item>

          {summary && (
            <Form.Item
              name={["user", "professionalSummary"]}
              label="Professional Summary"
              rules={[
                { required: true, message: "Professional summary is required" },
              ]}
            >
              <Input.TextArea rows={4} />
            </Form.Item>
          )}

          <Flex
            align="center"
            justify="space-around"
            style={{ width: "100%" }}
            wrap
          >
            <Form.Item
              wrapperCol={{ span: 24 }}
              style={{ textAlign: "center" }}
            >
              <StyledButton
                style={{ width: "100%" }}
                onClick={() => {
                  form
                    .validateFields()
                    .then(() => {
                      setCount((c) => c + 1);
                      const user = form.getFieldsValue(true)?.user;
                      generateProfessionalSummary.mutate(user);
                      setLoading(true);
                    })
                    .catch((error) => console.log(error));
                }}
                disabled={loading || count >= 3}
              >
                {loading
                  ? "Generating Summary..."
                  : count === 0
                    ? "Generate Summary"
                    : "Generate new summary"}
              </StyledButton>
            </Form.Item>

            {summary && (
              <Form.Item
                wrapperCol={{ span: 24 }}
                style={{ textAlign: "center" }}
              >
                <StyledButton htmlType="submit" type="submit">
                  Next
                </StyledButton>
              </Form.Item>
            )}
          </Flex>
        </Form>
      </Flex>
    </Flex>
  );
};
